import config from '../config'

const PING_TIMEOUT = 5_000
const PING_FRESHNESS_MS = 5_000

/**
 * URL for connectivity HEAD checks.
 * - Override with `REACT_APP_CONNECTIVITY_PING_URL` (full URL).
 * - Default: `{API_URL}/ping` (see Laravel `GET|HEAD /api/ping`).
 */
function resolvePingUrl(): string {
  const fromEnv = process.env.REACT_APP_CONNECTIVITY_PING_URL?.trim()
  if (fromEnv) return fromEnv
  const base = config.api.API_URL.replace(/\/$/, '')
  return `${base}/ping`
}

let _lastKnown: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true
let _inFlight: Promise<boolean> | null = null
let _lastCheckedAt = 0

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    _lastKnown = true
    _lastCheckedAt = 0
  })
  window.addEventListener('offline', () => {
    _lastKnown = false
    _lastCheckedAt = 0
    void checkConnectivity()
  })
}

/**
 * Performs a real HEAD request to verify connectivity.
 * Concurrent calls are deduplicated – they all resolve with the same result.
 * Updates the cached `_lastKnown` value used by `getLastKnownOnline()`.
 */
export async function checkConnectivity(): Promise<boolean> {
  if (_inFlight) return _inFlight
  if (_lastCheckedAt > 0 && Date.now() - _lastCheckedAt < PING_FRESHNESS_MS) {
    return _lastKnown
  }

  _inFlight = (async () => {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), PING_TIMEOUT)
    try {
      const response = await fetch(resolvePingUrl(), {
        method: 'HEAD',
        cache: 'no-store',
        signal: ctrl.signal
      })
      if (!response.ok) throw new Error(`Connectivity check failed with HTTP ${response.status}`)
      _lastKnown = true
      return true
    } catch {
      _lastKnown = false
      return false
    } finally {
      clearTimeout(timer)
      _lastCheckedAt = Date.now()
      _inFlight = null
    }
  })()

  return _inFlight
}

/**
 * Synchronous accessor for the last known connectivity state.
 * Safe to call from Redux middleware (synchronous context).
 * Kept up-to-date by the `online`/`offline` event listeners above
 * and by every `checkConnectivity()` call.
 */
export function getLastKnownOnline(): boolean {
  return _lastKnown
}
